import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Box,
  Button,
  Flex,
  Heading,
  LoadingOrbit,
  Select,
  Text,
  TextField,
  TextArea,
  type SelectOption,
} from '@chatsdet/ui';
import { Play, X } from '@phosphor-icons/react';
import { DynamicResultRenderer } from '../DynamicResultRenderer';
import {
  type DashboardCard,
  type RenderType,
  type UpdateCardInput,
  useRunCardQuery,
  useUpdateDashboardCard,
} from './useDashboardData';

const RENDER_TYPE_OPTIONS: SelectOption[] = [
  { id: 'bar_chart', label: 'Bar chart' },
  { id: 'line_chart', label: 'Line chart' },
  { id: 'pie_chart', label: 'Pie chart' },
  { id: 'big_number', label: 'Big number' },
  { id: 'table', label: 'Table' },
];

type Props = {
  isOpen: boolean;
  card: DashboardCard | null;
  dashboardId: string;
  onClose: () => void;
};

/**
 * Slide-up overlay for editing a dashboard card. Mirrors the FlowDesigner
 * PlaybookOverlay/WorkflowSettingsOverlay pattern: framer-motion y:'100%'→0
 * within a relatively-positioned parent.
 */
export function DashboardCardEditor({ isOpen, card, dashboardId, onClose }: Props) {
  return (
    <AnimatePresence>
      {isOpen && card && (
        <DashboardCardEditorInner
          card={card}
          dashboardId={dashboardId}
          onClose={onClose}
        />
      )}
    </AnimatePresence>
  );
}

function DashboardCardEditorInner({
  card,
  dashboardId,
  onClose,
}: {
  card: DashboardCard;
  dashboardId: string;
  onClose: () => void;
}) {
  const [title, setTitle] = React.useState(card.title);
  const [sql, setSql] = React.useState(card.sql);
  const [renderType, setRenderType] = React.useState<RenderType>(card.renderType);
  const [xKey, setXKey] = React.useState(card.xKey ?? '');
  const [yKey, setYKey] = React.useState(card.yKey ?? '');
  const [nameKey, setNameKey] = React.useState(card.nameKey ?? '');
  const [previewSql, setPreviewSql] = React.useState<string | null>(card.sql);
  const updateMutation = useUpdateDashboardCard(dashboardId);
  const preview = useRunCardQuery(previewSql ?? '', !!previewSql);

  // Reset local state if a different card is opened.
  React.useEffect(() => {
    setTitle(card.title);
    setSql(card.sql);
    setRenderType(card.renderType);
    setXKey(card.xKey ?? '');
    setYKey(card.yKey ?? '');
    setNameKey(card.nameKey ?? '');
    setPreviewSql(card.sql);
  }, [card.id]);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const save = () => {
    const patch: UpdateCardInput = {
      title,
      sql,
      renderType,
      xKey: xKey.trim() ? xKey.trim() : null,
      yKey: yKey.trim() ? yKey.trim() : null,
      nameKey: nameKey.trim() ? nameKey.trim() : null,
    };
    updateMutation.mutate({ cardId: card.id, patch }, { onSuccess: onClose });
  };

  return (
    <>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: [0.32, 0.72, 0, 1] } }}
        className="absolute inset-0 z-40 bg-background"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-4 left-4 z-50"
      >
        <Button
          variant="ghost"
          size="sm"
          onPress={onClose}
          className="w-9 p-0"
          aria-label="Close editor"
        >
          <X size={20} weight="bold" />
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex flex-col items-center overflow-hidden"
      >
        <div className="w-full max-w-5xl px-6 pt-6 pb-3 shrink-0 flex items-center justify-between">
          <Heading level={3}>Edit card</Heading>
          <Flex gap="sm">
            <Button variant="ghost" onPress={onClose}>
              Cancel
            </Button>
            <Button
              onPress={save}
              isDisabled={updateMutation.isPending || !title.trim() || !sql.trim()}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </Flex>
        </div>

        <div className="w-full max-w-5xl flex-1 min-h-0 px-6 pb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Box className="flex flex-col gap-3 overflow-auto rounded-lg border border-border bg-card p-4">
            <TextField label="Title" value={title} onChange={setTitle} />
            <Flex gap="sm" className="items-end flex-wrap">
              <Box className="min-w-[160px]">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Render
                </Text>
                <Select
                  items={RENDER_TYPE_OPTIONS}
                  selectedKey={renderType}
                  onSelectionChange={(v) => setRenderType(v as RenderType)}
                />
              </Box>
              <TextField label="x key" value={xKey} onChange={setXKey} className="flex-1 min-w-[120px]" />
              <TextField label="y key" value={yKey} onChange={setYKey} className="flex-1 min-w-[120px]" />
              <TextField
                label="name key"
                value={nameKey}
                onChange={setNameKey}
                className="flex-1 min-w-[120px]"
              />
            </Flex>
            <Box className="flex-1 min-h-[280px] flex flex-col">
              <Flex justify="between" align="center" className="mb-1">
                <Text size="xs" className="text-muted-foreground">
                  SQL
                </Text>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setPreviewSql(sql)}
                  isDisabled={!sql.trim()}
                >
                  <Play size={12} /> Run preview
                </Button>
              </Flex>
              <TextArea
                value={sql}
                onChange={setSql}
                rows={14}
                className="font-mono text-sm flex-1"
              />
            </Box>
          </Box>

          <Box className="flex flex-col rounded-lg border border-border bg-card p-4 overflow-hidden">
            <Text size="xs" className="text-muted-foreground mb-2 block">
              Preview
            </Text>
            <Box className="flex-1 overflow-auto">
              {!previewSql ? (
                <Flex align="center" justify="center" className="h-full">
                  <Text className="text-muted-foreground text-sm">
                    Run preview to see results.
                  </Text>
                </Flex>
              ) : preview.isFetching ? (
                <Flex align="center" justify="center" className="h-full">
                  <LoadingOrbit size="sm" />
                </Flex>
              ) : preview.data?.ok === false ? (
                <Text className="text-destructive font-mono text-xs whitespace-pre-wrap">
                  {preview.data.error}
                </Text>
              ) : preview.data ? (
                <DynamicResultRenderer
                  result={{
                    renderType,
                    title,
                    xKey: xKey || undefined,
                    yKey: yKey || undefined,
                    nameKey: nameKey || undefined,
                    data: preview.data.data,
                  }}
                />
              ) : null}
            </Box>
          </Box>
        </div>
      </motion.div>
    </>
  );
}
